import { format } from "date-fns";
import { useRef } from "react";
import { useReactToPrint } from "react-to-print";
import { FileIcon, SaveIcon } from "~/assets/icons";
import VeilighDarkLogo from "~/assets/logos/veiligh_new_logo_dark.png";

import {
  Button,
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "~/components/ui";
import TheAduseiEditorViewer from "~/components/ui/adusei-editor/viewer";
import { QUESTION_TYPES, DEFAULT_API_PAYLOAD } from "~/lib/constants";
import { isDocument, isImage, toSentenceCase } from "~/lib/utils";
import { restApi } from "~/redux/restApi";

import StepWrapper from "../../../incidents/create/steps/wrapper";
import { Attachment, Inspection } from "../../types";

interface Props {
  isOpen: boolean;
  onClose: () => void;
  singleInspection?: Inspection;
  inspectionId?: string;
}

export default function ExportPreview({
  isOpen,
  onClose,
  singleInspection: propsSingleInspection,
  inspectionId,
}: Props) {
  const contentRef = useRef<HTMLDivElement>(null);

  const { data } = restApi.useGetInspectionByIdQuery(
    { ...DEFAULT_API_PAYLOAD, id: inspectionId as string },
    { skip: !inspectionId },
  );

  const singleInspection = propsSingleInspection || data?.result;
  const pageStyle = `
    @page {
      margin: 10mm 15mm 10mm 15mm;
    }
      body {
    padding: 20px;
    margin: 7px;
  }
    `;

  const reactToPrintFn = useReactToPrint({
    contentRef,
    documentTitle: singleInspection?.title,

    // documentTitle: "Print Test",

    pageStyle,
  });

  function handleClose() {
    onClose();
  }

  return (
    <Dialog open={isOpen} onOpenChange={handleClose}>
      <DialogContent className="max-w-200 w-200 px-1" noClose>
        <DialogHeader>
          <DialogTitle>
            <Button
              className="absolute -right-24 -top-0"
              type="button"
              onClick={() => reactToPrintFn()}
            >
              <div className="flex gap-2">
                <SaveIcon />
                <p>Save</p>
              </div>
            </Button>
          </DialogTitle>
        </DialogHeader>
        <div className="space-y-6 px-8">
          <div ref={contentRef}>
            <div>
              <div className="flex h-7 flex-row items-center justify-between">
                <div className="w-32 p-5 py-2 text-center">
                  <img src={VeilighDarkLogo} alt="Logo" />
                </div>
                <p className="m-0 text-preview-sm leading-preview text-neutral-400 underline">
                  www.veiligh.com
                </p>
              </div>
              <div className="w-full bg-neutral-300">
                <hr />
              </div>
            </div>

            <StepWrapper className="mt-4 w-full bg-neutral-100">
              <div className="space-y-6">
                <div className="space-y-2">
                  <span className="block text-sm font-normal text-neutral-400"></span>
                  <span className="block font-Medium text-sm leading-preview-sm text-primary-500">
                    {toSentenceCase(singleInspection?.title)}{" "}
                  </span>
                </div>
                <div></div>
                <div className="flex w-full items-center justify-between">
                  <div className="space-y-2">
                    <span className="block text-preview-sm-2 font-normal leading-preview-sm-2 text-neutral-400">
                      Inspection Type
                    </span>
                    <span className="block text-preview-sm-2 font-normal leading-preview-sm-2 text-primary-500">
                      {toSentenceCase(singleInspection?.type?.name)}
                    </span>
                  </div>
                  <div className="space-y-2">
                    <span className="block text-preview-sm-2 font-normal leading-preview-sm-2 text-neutral-400">
                      Site/Location
                    </span>
                    <span className="block text-preview-sm-2 font-normal leading-preview-sm-2 text-primary-500">
                      {singleInspection?.sites
                        ?.map((site) => toSentenceCase(site.name))
                        .join(", ")}{" "}
                    </span>
                  </div>
                  <div className="space-y-2">
                    <span className="block text-preview-sm-2 font-normal leading-preview-sm-2 text-neutral-400">
                      Due Date
                    </span>
                    <span className="block text-preview-sm-2 font-normal leading-preview-sm-2 text-primary-500">
                      {singleInspection?.createdAt
                        ? format(
                            singleInspection?.createdAt,
                            "MMM d, yyyy. h:mma",
                          )
                        : ""}
                    </span>
                  </div>
                  <div className="space-y-2">
                    <span className="block text-preview-sm-2 font-normal leading-preview-sm-2 text-neutral-400">
                      Assignee
                    </span>
                    <span className="block text-preview-sm-2 font-normal leading-preview-sm-2 text-primary-500">
                      {toSentenceCase(
                        singleInspection?.responsiblePerson?.name,
                      )}
                    </span>
                  </div>
                </div>
                <div className="w-full space-y-2">
                  {!singleInspection?.description ? (
                    <div>{""}</div>
                  ) : (
                    <>
                      <span className="block text-preview-sm font-normal leading-preview text-neutral-400">
                        Notes
                      </span>
                      <span className="block text-preview-sm font-normal leading-preview text-primary-500">
                        <TheAduseiEditorViewer
                          content={
                            toSentenceCase(singleInspection?.description) ?? ""
                          }
                        />
                      </span>
                    </>
                  )}
                </div>
              </div>
            </StepWrapper>
            <div className="mt-4">
              <div className="w-full">
                {singleInspection?.formResponse?.form?.sections &&
                singleInspection.formResponse.form.sections.length > 0 ? (
                  <div className="space-y-8">
                    {singleInspection.formResponse.form.sections
                      .slice()
                      .sort((a: any, b: any) => (a.rank || 0) - (b.rank || 0))
                      .map((section, sectionIdx) => {
                        const sectionFieldIds =
                          section.formFields?.map((f) => f.id) || [];
                        const sectionResponses =
                          singleInspection.formResponse?.formResponses?.filter(
                            (r) => sectionFieldIds.includes(r.formField?.id),
                          ) || [];

                        if (sectionResponses.length === 0 && !section.title)
                          return null;

                        return (
                          <div
                            key={section.id || sectionIdx}
                            className="space-y-4 break-inside-avoid"
                          >
                            <div className="bg-neutral-100 px-4 py-2 rounded-t-lg border-b-2 border-primary-500">
                              <h3 className="text-lg font-bold text-primary-500 uppercase tracking-wider">
                                {section.title || `Section ${sectionIdx + 1}`}
                              </h3>
                              {section.description && (
                                <p className="text-sm text-neutral-500 mt-1">
                                  {section.description}
                                </p>
                              )}
                            </div>
                            <ul className="space-y-4">
                              {sectionResponses.map((response, idx) => {
                                const attachments = response?.attachments || [];
                                const images = attachments.filter(
                                  (attachment: Attachment) =>
                                    isImage(attachment.name),
                                );
                                const documents = attachments.filter(
                                  (attachment: Attachment) =>
                                    isDocument(attachment.name),
                                );

                                return (
                                  <li key={idx}>
                                    <div className="rounded-lg border border-neutral-200 bg-background px-8 py-4 shadow-sm">
                                      <div className="flex items-center justify-between">
                                        <div className="text-left w-full">
                                          <div className="flex flex-1 gap-2 text-preview-lg capitalize leading-preview-lg text-primary-500 font-semibold mb-2">
                                            <span>{idx + 1}.</span>
                                            <span>
                                              {
                                                response?.formField?.question
                                                  ?.label
                                              }
                                            </span>
                                          </div>
                                          <div className="pl-6">
                                            <span className="text-preview-sm-2 leading-preview-lg text-neutral-600 block">
                                              {response?.formField?.question
                                                ?.type?.name ===
                                              QUESTION_TYPES.DATE ? (
                                                response?.value ? (
                                                  format(
                                                    new Date(
                                                      response?.value as string,
                                                    ),
                                                    "MMM d, yyyy",
                                                  )
                                                ) : (
                                                  ""
                                                )
                                              ) : response?.formField?.question
                                                  ?.type?.name ===
                                                QUESTION_TYPES.PARAGRAPH ? (
                                                <TheAduseiEditorViewer
                                                  content={
                                                    response?.value ?? ""
                                                  }
                                                />
                                              ) : (
                                                response?.value || (
                                                  <span className="text-neutral-300 italic">
                                                    No answer provided
                                                  </span>
                                                )
                                              )}
                                            </span>

                                            {response?.notes && (
                                              <div className="mt-2 text-sm text-neutral-500 italic bg-neutral-50 p-2 rounded-md border border-neutral-100">
                                                <span className="font-semibold not-italic text-neutral-400 text-xs uppercase tracking-wider block mb-1">
                                                  Notes / Remarks
                                                </span>
                                                {response.notes}
                                              </div>
                                            )}

                                            <div>
                                              <div className="space-y-4">
                                                {/* Images */}
                                                {images.length > 0 && (
                                                  <div className="mt-4 border-t pt-3 border-neutral-100">
                                                    <p className="mb-2 text-xs font-semibold uppercase tracking-wider text-neutral-400">
                                                      Attached Images
                                                    </p>
                                                    <div className="grid grid-cols-4 gap-3">
                                                      {images.map(
                                                        (image: Attachment) => (
                                                          <img
                                                            key={image.id}
                                                            src={image.link}
                                                            alt={image.name}
                                                            className="h-[120px] w-full rounded-xl object-cover border border-neutral-200 shadow-sm"
                                                          />
                                                        ),
                                                      )}
                                                    </div>
                                                  </div>
                                                )}

                                                {/* Documents */}
                                                {documents.length > 0 && (
                                                  <div className="mt-4 border-t pt-3 border-neutral-100">
                                                    <p className="mb-2 text-xs font-semibold uppercase tracking-wider text-neutral-400">
                                                      Attached Documents
                                                    </p>
                                                    <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
                                                      {documents.map(
                                                        (doc: Attachment) => (
                                                          <div
                                                            key={doc.id}
                                                            className="flex items-center gap-2 bg-neutral-50 p-2 rounded-md border border-neutral-200"
                                                          >
                                                            <FileIcon />
                                                            <a
                                                              href={doc.link}
                                                              target="_blank"
                                                              rel="noopener noreferrer"
                                                              className="text-xs font-medium text-info-600 hover:underline truncate"
                                                            >
                                                              {doc.name}
                                                            </a>
                                                          </div>
                                                        ),
                                                      )}
                                                    </div>
                                                  </div>
                                                )}
                                              </div>
                                            </div>
                                          </div>
                                        </div>
                                      </div>
                                    </div>
                                  </li>
                                );
                              })}
                            </ul>
                          </div>
                        );
                      })}
                  </div>
                ) : (
                  <ul className="space-y-4">
                    {singleInspection?.formResponse?.formResponses?.map(
                      (response, idx) => {
                        // Dynamically define images and documents for each response
                        const attachments = response?.attachments || [];
                        const images = attachments.filter(
                          (attachment: Attachment) => isImage(attachment.name),
                        );
                        const documents = attachments.filter(
                          (attachment: Attachment) =>
                            isDocument(attachment.name),
                        );

                        return (
                          <li key={idx} className="break-inside-avoid">
                            <div className="rounded-lg border border-neutral-200 bg-background px-8 py-4 shadow-sm">
                              <div className="flex items-center justify-between">
                                <div className="text-left w-full">
                                  <div className="flex flex-1 gap-2 text-preview-lg capitalize leading-preview-lg text-primary-500 font-semibold mb-2">
                                    <span>{idx + 1}.</span>
                                    <span>
                                      {response?.formField?.question?.label}
                                    </span>
                                  </div>
                                  <div className="pl-6">
                                    <span className="text-preview-sm-2 leading-preview-lg text-neutral-600 block">
                                      {response?.formField?.question?.type
                                        ?.name === QUESTION_TYPES.DATE ? (
                                        response?.value ? (
                                          format(
                                            new Date(response?.value as string),
                                            "MMM d, yyyy",
                                          )
                                        ) : (
                                          ""
                                        )
                                      ) : response?.formField?.question?.type
                                          ?.name ===
                                        QUESTION_TYPES.PARAGRAPH ? (
                                        <TheAduseiEditorViewer
                                          content={response?.value ?? ""}
                                        />
                                      ) : (
                                        response?.value || (
                                          <span className="text-neutral-300 italic">
                                            No answer provided
                                          </span>
                                        )
                                      )}
                                    </span>

                                    {response?.notes && (
                                      <div className="mt-2 text-sm text-neutral-500 italic bg-neutral-50 p-2 rounded-md border border-neutral-100">
                                        <span className="font-semibold not-italic text-neutral-400 text-xs uppercase tracking-wider block mb-1">
                                          Notes / Remarks
                                        </span>
                                        {response.notes}
                                      </div>
                                    )}

                                    <div>
                                      <div className="space-y-4">
                                        {/* Images */}
                                        {images.length > 0 && (
                                          <div className="mt-4 border-t pt-3 border-neutral-100">
                                            <p className="mb-2 text-xs font-semibold uppercase tracking-wider text-neutral-400">
                                              Attached Images
                                            </p>
                                            <div className="grid grid-cols-4 gap-3">
                                              {images.map(
                                                (image: Attachment) => (
                                                  <img
                                                    key={image.id}
                                                    src={image.link}
                                                    alt={image.name}
                                                    className="h-[120px] w-full rounded-xl object-cover border border-neutral-200 shadow-sm"
                                                  />
                                                ),
                                              )}
                                            </div>
                                          </div>
                                        )}

                                        {/* Documents */}
                                        {documents.length > 0 && (
                                          <div className="mt-4 border-t pt-3 border-neutral-100">
                                            <p className="mb-2 text-xs font-semibold uppercase tracking-wider text-neutral-400">
                                              Attached Documents
                                            </p>
                                            <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
                                              {documents.map(
                                                (doc: Attachment) => (
                                                  <div
                                                    key={doc.id}
                                                    className="flex items-center gap-2 bg-neutral-50 p-2 rounded-md border border-neutral-200"
                                                  >
                                                    <FileIcon />
                                                    <a
                                                      href={doc.link}
                                                      target="_blank"
                                                      rel="noopener noreferrer"
                                                      className="text-xs font-medium text-info-600 hover:underline truncate"
                                                    >
                                                      {doc.name}
                                                    </a>
                                                  </div>
                                                ),
                                              )}
                                            </div>
                                          </div>
                                        )}
                                      </div>
                                    </div>
                                  </div>
                                </div>
                              </div>
                            </div>
                          </li>
                        );
                      },
                    )}
                  </ul>
                )}
              </div>
            </div>
          </div>
        </div>
        <DialogFooter className="mt-5 justify-end gap-2"></DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
